/// <reference types="vite/client" />
// In dev, Vite proxies /api → https://bot-api.kha.do (see vite.config.ts).
// In prod the SPA is served from bot.kha.do/ and calls the API directly.
// Exported so non-JSON fetches (e.g. auth probes, file uploads) hit the same
// origin where the session cookie lives; calling a different origin drops
// credentials and looks like the user is logged out after every refresh.
export const API_BASE = import.meta.env.DEV ? "/api" : "https://bot-api.kha.do";

export async function apiJson<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    ...init,
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      ...(init?.headers ?? {}),
    },
  });

  if (res.status === 401) {
    // Redirect to login preserving the intended path so the login page can
    // navigate back after a successful auth.
    window.location.href = "/login";
    throw new Error("Unauthorized");
  }

  if (!res.ok) {
    // Prefer the backend's JSON {error: "..."} for precise messages. If the
    // response is HTML (CF tunnel origin-unreachable page, CF Pages default
    // 404 "The object can not be found here", nginx error page, etc.) use a
    // generic "service temporarily unavailable" message instead of leaking
    // the CF text — it confuses users who think their data is missing.
    const ct = res.headers.get("content-type") ?? "";
    let message = res.statusText || "Request failed";
    if (ct.includes("application/json")) {
      try {
        const body = (await res.json()) as { error?: string };
        if (body.error) message = body.error;
      } catch { /* ignore */ }
    } else {
      // Non-JSON response (usually HTML from an upstream proxy). Drop the
      // body entirely — react-query will retry transient errors.
      message = res.status >= 500 || res.status === 0
        ? "Service temporarily unavailable"
        : `Request failed (${res.status})`;
    }
    throw new Error(message);
  }

  return res.json() as Promise<T>;
}

export function apiGet<T>(path: string): Promise<T> {
  return apiJson<T>(path, { method: "GET" });
}

export function apiPost<T>(path: string, body?: unknown): Promise<T> {
  return apiJson<T>(path, {
    method: "POST",
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
}

export function apiPatch<T>(path: string, body?: unknown): Promise<T> {
  return apiJson<T>(path, {
    method: "PATCH",
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
}

export function apiDelete<T>(path: string, body?: unknown): Promise<T> {
  return apiJson<T>(path, {
    method: "DELETE",
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
}
